import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../components/Header";
import styled from "styled-components";
import { Common } from "../../styles/Common";
import Box from "../../assets/Signup/Box.png";
import SearchIcon from "@src/assets/Signup/Search";

import OfficeInfoCard, { OfficeItem } from "../../components/SignupExpert/OfficeInfoCard";
import OfficeSummaryBox, { OfficeSummary } from "../../components/SignupExpert/OfficeSummaryBox";
import { useAgentOffices, AgentOfficeItem, AgentOfficeParams } from "../../api/expert/expertApi";
import { useSignupExpertStore } from "@src/store/signupExpertStore";


type OfficeSearchProps = {
    sggCode: string;
};

const OfficeSearch: React.FC<OfficeSearchProps> = ({ sggCode }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const fromPath: string = (location.state as any)?.from ?? "/signup/expert";

    const [query, setQuery] = useState<string>("");
    const [searched, setSearched] = useState<boolean>(false);
    const [selected, setSelected] = useState<OfficeItem | null>(null);
    const [params, setParams] = useState<AgentOfficeParams>({
        sggCode,
        name: "",
        limit: 0,
        cursor: null,
    });

    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    // 자동 검색
    useEffect(() => {
        if (!query.trim()) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSelected(null);
            setSearched(true);
            setParams({
                sggCode,
                name: query.trim(),
                limit: 20,
                cursor: null,
            });
        }, 500); // 입력 없으면 0.5초 후 검색
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query]);

    // 기존 검색 (버튼 & 엔터 클릭 검색)
    const handleSearch = (e?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
        if (e) e.preventDefault();
        const name = query.trim();
        if (!name) return;
        setSelected(null);
        setSearched(true);
        setParams({
            sggCode,
            name,
            limit: 20,
            cursor: null,
        });
    };

    const { data, isFetching, isError } = useAgentOffices(params);

    const results: OfficeItem[] = useMemo(() => {
        if (!data?.list) return [];
        return data.list.map(map_to_office_item);
    }, [data]);

    const hasResults = searched && results.length > 0;

    const { form, setPartialState } = useSignupExpertStore();
    const handleComplete = () => {
        if (!searched) return;

        if (!hasResults) {
            navigate("/signup/writeOffice");
            return;
        }

        if (selected) {
            setPartialState({
                form: {
                    ...form,
                    officeInfo: {
                        ...form.officeInfo,
                        name: selected.name ?? "",
                        ceo: selected.ceo ?? "",
                        address: selected.address ?? "",
                        phone: selected.phone ?? "",
                        registrationNumber: selected.registrationNumber ?? "",
                    },
                },
            });
            navigate(-1);
        }
    };

    const buttonLabel = !searched ? "완료" : hasResults ? "완료" : "직접 입력하기";
    const isActive = !searched ? false : hasResults ? !!selected : !!query.trim();

    return (
        <Layout>
            <Header title="중개사무소 조회" />
            <Container>
                <form onSubmit={handleSearch}>
                    <SearchBar>
                        <IconButton type="button" aria-label="검색" onClick={handleSearch}>
                            <SearchIcon />
                        </IconButton>
                        <SearchInput
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="중개사무소명을 입력하세요"
                            autoComplete="off"
                        />
                    </SearchBar>
                </form>

                {hasResults && !selected && !isFetching && (
                    <List>
                        {results.map((r) => (
                            <li key={r.id}>
                                <OfficeSummaryBox
                                    item={
                                        {
                                            id: r.id,
                                            name: r.name,
                                            ceo: r.ceo,
                                            address: r.address,
                                        } as OfficeSummary
                                    }
                                    onClick={() => setSelected(r)}
                                />
                            </li>
                        ))}
                    </List>
                )}

                {selected && <OfficeInfoCard item={selected} />}

                {searched && !hasResults && !isFetching && !isError && (
                    <EmptyState>
                        <img src={Box} alt="검색 결과 없음" />
                        <p>
                            일치하는 정보가 없어요!
                            <br />
                            직접 입력해 주세요.
                        </p>
                    </EmptyState>
                )}
            </Container>

            <Footer>
                <PrimaryBtn type="button" className={isActive ? "active" : ""} onClick={handleComplete} disabled={!isActive}>
                    {buttonLabel}
                </PrimaryBtn>
            </Footer>
        </Layout>
    );
};

export default OfficeSearch;

function map_to_office_item(x: AgentOfficeItem): OfficeItem {
    return {
        id: String(x.no),
        name: x.name,
        ceo: x.ceo,
        address: x.address,
        phone: x.phone,
        registrationNumber: x.registrationNumber,
    };
}

const Layout = styled.div``;

const Container = styled.div`
    padding: 24px;
    position: relative;
    height: 100vh;
    padding-bottom: 120px;
    overflow-y: scroll;
`;

const SearchBar = styled.div`
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #2275ee;
    background-color: #fbf8f8;
    border-radius: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const IconButton = styled.button`
    display: inline-flex;
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;

    svg {
        width: 100%;
        height: 100%;
        display: block;
    }
`;

const SearchInput = styled.input`
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 16px;
    line-height: 1.4;
    color: ${Common.colors?.normalTextColor ?? "#111"};
    ::placeholder {
        color: #9da6b3;
    }
`;

const List = styled.ul`
    margin-top: 16px;
    li + li {
        margin-top: -1px;
    }
`;

const Footer = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    padding: 24px;
`;

const PrimaryBtn = styled.button`
    width: 100%;
    padding: 14px 16px;
    border-radius: 12px;
    background: #c3c3c3;
    border: 1px solid #c3c3c3;
    color: #ffffff;
    font-weight: 400;
    font-size: 18px;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.8;
    }

    &.active {
        background: ${Common.colors.aucMarkerColor};
        border: 1px solid ${Common.colors.aucMarkerColor};
        color: #ffffff;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: center;
    align-items: center;
    color: #333;
    font-size: 18px;
    font-weight: 400;
    line-height: 140%;
    min-height: calc(100vh - 60px - 120px);

    img {
        width: 124px;
        height: 83px;
        margin-bottom: 12px;
    }
`;
